import type { SequenceKind } from "@lib/genetics";

/**
 * Sequences shorter than this are editable in the sequence editor. Longer
 * sequences are read-only with chunk-based loading.
 */
export const EDITABLE_LIMIT = 500_000;

/**
 * Default chunk size in nucleotides (1M).
 *
 * Each chunk stores this many nucleotides, encoded to half as many bytes
 * (~512KB on disk). All sequences use chunked storage with this size.
 */
export const DEFAULT_CHUNK_SIZE = 1_048_576;

export const NT_TO_NIBBLE: Record<string, number> = {
  A: 0,
  T: 1,
  G: 2,
  C: 3,
  U: 4,
  R: 5,
  Y: 6,
  S: 7,
  W: 8,
  K: 9,
  M: 10,
  B: 11,
  D: 12,
  H: 13,
  V: 14,
  N: 15,
};

export const VALID_NUCLEOTIDES = new Set(Object.keys(NT_TO_NIBBLE));

export const AA_TO_BYTE: Record<string, number> = {
  A: 0,
  C: 1,
  D: 2,
  E: 3,
  F: 4,
  G: 5,
  H: 6,
  I: 7,
  K: 8,
  L: 9,
  M: 10,
  N: 11,
  P: 12,
  Q: 13,
  R: 14,
  S: 15,
  T: 16,
  V: 17,
  W: 18,
  Y: 19,
  B: 20,
  Z: 21,
  X: 22,
  "*": 23,
  "-": 24,
};

export const VALID_AMINO_ACIDS = new Set(Object.keys(AA_TO_BYTE));

const BYTE_TO_AA = Object.entries(AA_TO_BYTE).reduce(
  (acc, [aa, byte]) => {
    acc[byte] = aa;
    return acc;
  },
  {} as Record<number, string>,
);

const NIBBLE_TO_NT: string[] = [
  "A",
  "T",
  "G",
  "C",
  "U",
  "R",
  "Y",
  "S",
  "W",
  "K",
  "M",
  "B",
  "D",
  "H",
  "V",
  "N",
];

// Char-code-indexed lookup table for nucleotide encoding. Avoids per-character
// object property lookups in the hot encoding loop.
const ntLut = new Uint8Array(128).fill(15);
for (const [ch, nibble] of Object.entries(NT_TO_NIBBLE)) {
  ntLut[ch.charCodeAt(0)] = nibble;
  ntLut[ch.toLowerCase().charCodeAt(0)] = nibble;
}

/**
 * Encode a nucleotide sequence string into a 4-bit packed Uint8Array.
 *
 * Supports all 16 IUPAC degenerate nucleotide codes. Packs two nucleotides per
 * byte for 2x compression.
 *
 * @param sequence - the nucleotide sequence to encode (case-insensitive)
 * @returns the encoded sequence as a Uint8Array
 */
export function encodeNucleotides(sequence: string): Uint8Array {
  const len = sequence.length;
  const bytes = new Uint8Array((len + 1) >> 1);

  for (let i = 0; i < len - 1; i += 2) {
    bytes[i >> 1] =
      (ntLut[sequence.charCodeAt(i)] << 4) | ntLut[sequence.charCodeAt(i + 1)];
  }

  if (len & 1) {
    bytes[len >> 1] = ntLut[sequence.charCodeAt(len - 1)] << 4;
  }

  return bytes;
}

/**
 * Decode a 4-bit packed Uint8Array back into a nucleotide sequence string.
 *
 * @param bytes - the encoded sequence bytes
 * @param length - the original sequence length (needed to handle odd-length sequences)
 * @returns the decoded nucleotide sequence string
 */
export function decodeNucleotides(bytes: Uint8Array, length: number): string {
  const chars: string[] = [];

  for (let i = 0; i < bytes.length; i++) {
    const high = (bytes[i] >> 4) & 0x0f;
    const low = bytes[i] & 0x0f;

    chars.push(NIBBLE_TO_NT[high]);

    if (chars.length < length) {
      chars.push(NIBBLE_TO_NT[low]);
    }
  }

  return chars.join("");
}

/**
 * Split a nucleotide sequence into encoded chunks for storage.
 *
 * @param sequence - the nucleotide sequence to chunk
 * @param chunkSize - nucleotides per chunk (defaults to DEFAULT_CHUNK_SIZE)
 * @returns array of encoded chunks
 */
export function chunkNucleotides(
  sequence: string,
  chunkSize: number = DEFAULT_CHUNK_SIZE,
): Uint8Array[] {
  const chunks: Uint8Array[] = [];

  for (let i = 0; i < sequence.length; i += chunkSize) {
    const chunkStr = sequence.slice(i, i + chunkSize);
    chunks.push(encodeNucleotides(chunkStr));
  }

  return chunks;
}

/**
 * Encode an amino acid sequence string into a Uint8Array.
 *
 * Uses 1 byte per amino acid (no compression).
 *
 * @param sequence - the amino acid sequence to encode
 * @returns the encoded sequence as a Uint8Array
 */
export function encodeAminoAcids(sequence: string): Uint8Array {
  const upper = sequence.toUpperCase();
  const bytes = new Uint8Array(upper.length);

  for (let i = 0; i < upper.length; i++) {
    bytes[i] = AA_TO_BYTE[upper[i]];
  }

  return bytes;
}

/**
 * Decode a Uint8Array back into an amino acid sequence string.
 *
 * @param bytes - the encoded sequence bytes
 * @returns the decoded amino acid sequence string
 */
export function getEncoder(
  sequenceKind: SequenceKind,
): (sequence: string) => Uint8Array {
  return sequenceKind === "aa" ? encodeAminoAcids : encodeNucleotides;
}

export function decodeAminoAcids(bytes: Uint8Array): string {
  const chars: string[] = [];

  for (let i = 0; i < bytes.length; i++) {
    chars.push(BYTE_TO_AA[bytes[i]]);
  }

  return chars.join("");
}
